import type { Block, Doc, Format, Inline, List, ListItem, MentionName } from "./ast";
import { charAt, charBefore, isAsciiPunct, isBoundary, isSpace, isWord } from "./chars";
import { autolinkEnd, safeHref } from "./links";

// A hand-written parser for the Rhema message format described in docs/message-format.md. It
// never throws and runs in time close to linear in the body, whatever the body holds.

export type ParseOptions = {
  /** Undefined reads the body as plain text: only addresses and mentions are picked out. */
  format?: Format;
  /** The members the message mentions, so `@Name` can be matched to them. */
  mentions?: readonly MentionName[];
};

type Mark = "*" | "_" | "~";
const MARK_TYPE = { "*": "bold", _: "italic", "~": "strike" } as const;
/** Link text and addresses longer than this are not read as links, which bounds the work. */
const MAX_LINK_TEXT = 1000;
const MAX_LINK_URL = 2048;
const CHANNEL = /[\p{L}\p{N}][\p{L}\p{N}\p{M}-]{0,79}/uy;

class InlineParser {
  /** Per search, the position up to which a search already failed, so it is never repeated. */
  private failed = new Map<string, number>();
  /** Where each [ closes, for the lines paired so far. */
  private brackets = new Map<number, number>();
  private bracketLines = new Set<number>();
  private names: MentionName[];

  constructor(
    private s: string,
    private md: boolean,
    mentions: readonly MentionName[],
  ) {
    // Longest first, so "@Ama Mensah" wins over "@Ama".
    this.names = mentions.filter((m) => m.name.length > 0).sort((a, b) => b.name.length - a.name.length);
  }

  private lineEnd(i: number, end: number): number {
    const nl = this.s.indexOf("\n", i);
    return nl === -1 || nl > end ? end : nl;
  }

  private runOf(i: number, ch: string, end: number): number {
    let j = i;
    while (j < end && this.s[j] === ch) j++;
    return j - i;
  }

  /** A code span opening at i: its text and where it ends. */
  private codeSpan(i: number, end: number): { text: string; end: number } | null {
    const s = this.s;
    const n = this.runOf(i, "`", end);
    const stop = this.lineEnd(i, end);
    const key = `\`${n}:${end}`;
    if ((this.failed.get(key) ?? -1) > i) return null;
    let j = i + n;
    while (j < stop) {
      if (s[j] !== "`") {
        j++;
        continue;
      }
      const run = this.runOf(j, "`", stop);
      if (run === n) {
        let text = s.slice(i + n, j);
        if (!text.length) return null;
        // As in CommonMark, one space on each side lets a span start or end with a backtick.
        if (text.length > 2 && text[0] === " " && text[text.length - 1] === " " && text.trim())
          text = text.slice(1, -1);
        return { text, end: j + n };
      }
      j += run;
    }
    this.failed.set(key, stop);
    return null;
  }

  /**
   * Pairs every [ on a line with its ], once per line, as a stack does: escaped brackets and
   * brackets inside code do not count. -1 for a [ with no partner.
   */
  private bracketMatch(i: number): number {
    const known = this.brackets.get(i);
    if (known !== undefined) return known;
    const s = this.s;
    const start = s.lastIndexOf("\n", i - 1) + 1;
    if (!this.bracketLines.has(start)) {
      this.bracketLines.add(start);
      const nl = s.indexOf("\n", start);
      const stop = nl === -1 ? s.length : nl;
      const open: number[] = [];
      let j = start;
      while (j < stop) {
        const c = s.charAt(j);
        if (c === "\\" && isAsciiPunct(s[j + 1])) {
          j += 2;
          continue;
        }
        if (c === "`") {
          const span = this.codeSpan(j, stop);
          j = span ? span.end : j + this.runOf(j, "`", stop);
          continue;
        }
        if (c === "[") {
          open.push(j);
          this.brackets.set(j, -1);
        } else if (c === "]") {
          const at = open.pop();
          if (at !== undefined) this.brackets.set(at, j);
        }
        j++;
      }
    }
    return this.brackets.get(i) ?? -1;
  }

  /** A [text](address) link opening at i. Null when it is not one or the address is not allowed. */
  private link(i: number, end: number): { href: string; from: number; to: number; end: number } | null {
    const s = this.s;
    const stop = this.lineEnd(i, end);
    const match = this.bracketMatch(i);
    const close = match < stop && match - i <= MAX_LINK_TEXT ? match : -1;
    if (close === -1) return null;
    if (close === i + 1 || s[close + 1] !== "(") return null;
    let parens = 0;
    let k = close + 2;
    const urlLimit = Math.min(stop, k + MAX_LINK_URL);
    for (; k < urlLimit; k++) {
      const c = s.charAt(k);
      if (isSpace(c)) return null;
      if (c === "(") parens++;
      else if (c === ")") {
        if (parens === 0) break;
        parens--;
      }
    }
    if (s[k] !== ")") return null;
    const href = safeHref(s.slice(close + 2, k));
    if (!href) return null;
    return { href, from: i + 1, to: close, end: k + 1 };
  }

  /** The matching closing mark for a mark opening at i, or -1. */
  private closer(c: Mark, i: number, end: number, inLink: boolean): number {
    const s = this.s;
    if (!isBoundary(charBefore(s, i), c)) return -1;
    const next = s[i + 1];
    if (i + 1 >= end || next === undefined || isSpace(next) || next === c) return -1;
    const key = `${c}:${end}:${inLink}`;
    if ((this.failed.get(key) ?? -1) > i) return -1;
    const stop = this.lineEnd(i, end);
    let j = i + 1;
    while (j < stop) {
      const ch = s.charAt(j);
      if (ch === "\\" && isAsciiPunct(s[j + 1])) {
        j += 2;
        continue;
      }
      if (ch === "`") {
        const span = this.codeSpan(j, stop);
        j = span ? span.end : j + this.runOf(j, "`", stop);
        continue;
      }
      if (ch === "[") {
        const link = this.link(j, stop);
        if (link) {
          j = link.end;
          continue;
        }
      }
      // Skip what the main loop would read as one piece, so both agree on where marks end.
      if (!inLink) {
        if (ch === "#") {
          const name = this.channel(j, stop);
          if (name !== -1) {
            j = name;
            continue;
          }
        }
        if (ch === "@" && this.names.length) {
          const m = this.mention(j, stop);
          if (m) {
            j += 1 + m.name.length;
            continue;
          }
        }
        if (ch === "h" || ch === "H" || ch === "m" || ch === "M") {
          const auto = autolinkEnd(s, j, stop);
          if (auto > 0) {
            j = auto;
            continue;
          }
        }
      }
      if (ch === c && j > i + 1 && !isSpace(s[j - 1]) && isBoundary(charAt(s, j + 1), c)) return j;
      j++;
    }
    this.failed.set(key, stop);
    return -1;
  }

  private mention(i: number, end: number): MentionName | null {
    const s = this.s;
    if (isWord(charBefore(s, i))) return null;
    for (const m of this.names) {
      const stop = i + 1 + m.name.length;
      if (stop <= end && s.startsWith(m.name, i + 1) && !isWord(charAt(s, stop))) return m;
    }
    return null;
  }

  private channel(i: number, end: number): number {
    const s = this.s;
    const before = charBefore(s, i);
    if (isWord(before) || before === "&" || before === "#" || before === "/") return -1;
    CHANNEL.lastIndex = i + 1;
    const m = CHANNEL.exec(s);
    if (!m) return -1;
    let stop = Math.min(i + 1 + m[0].length, end);
    while (stop > i + 1 && s[stop - 1] === "-") stop--;
    return stop > i + 1 ? stop : -1;
  }

  parse(start: number, end: number, open: ReadonlySet<string>, inLink: boolean): Inline[] {
    const s = this.s;
    const out: Inline[] = [];
    let buf = "";
    const flush = () => {
      if (buf) out.push({ type: "text", text: buf });
      buf = "";
    };
    let i = start;
    while (i < end) {
      const c = s.charAt(i);
      if (this.md) {
        if (c === "\\" && i + 1 < end && isAsciiPunct(s[i + 1])) {
          buf += s[i + 1];
          i += 2;
          continue;
        }
        if (c === "`") {
          const span = this.codeSpan(i, end);
          if (span) {
            flush();
            out.push({ type: "code", text: span.text });
            i = span.end;
          } else {
            const run = this.runOf(i, "`", end);
            buf += s.slice(i, i + run);
            i += run;
          }
          continue;
        }
        if (c === "[" && !inLink) {
          const link = this.link(i, end);
          if (link) {
            flush();
            out.push({
              type: "link",
              href: link.href,
              children: this.parse(link.from, link.to, open, true),
            });
            i = link.end;
            continue;
          }
        }
        if ((c === "*" || c === "_" || c === "~") && !open.has(c)) {
          const close = this.closer(c, i, end, inLink);
          if (close !== -1) {
            flush();
            const inner = new Set(open);
            inner.add(c);
            out.push({ type: MARK_TYPE[c], children: this.parse(i + 1, close, inner, inLink) });
            i = close + 1;
            continue;
          }
        }
        if (c === "#" && !inLink) {
          const stop = this.channel(i, end);
          if (stop !== -1) {
            flush();
            out.push({ type: "channel", name: s.slice(i + 1, stop) });
            i = stop;
            continue;
          }
        }
      }
      if (!inLink && (c === "h" || c === "H" || c === "m" || c === "M")) {
        const stop = autolinkEnd(s, i, end);
        if (stop > 0) {
          flush();
          const href = s.slice(i, stop);
          out.push({ type: "link", href, auto: true, children: [{ type: "text", text: href }] });
          i = stop;
          continue;
        }
      }
      if (c === "@" && !inLink && this.names.length) {
        const m = this.mention(i, end);
        if (m) {
          flush();
          out.push({ type: "mention", id: m.id, name: m.name });
          i += 1 + m.name.length;
          continue;
        }
      }
      buf += c;
      i++;
    }
    flush();
    return out;
  }
}

function inline(text: string, md: boolean, mentions: readonly MentionName[]): Inline[] {
  return new InlineParser(text, md, mentions).parse(0, text.length, new Set(), false);
}

// Block structure, read line by line.

const FENCE_OPEN = /^ {0,3}(`{3,})(.*)$/;
const LANG = /^\s*([\w#+.-]{0,32})\s*$/;
const QUOTE = /^ {0,3}>(?: (.*)|$)/;
const ITEM = /^([ \t]*)([-*]|\d{1,9}\.) (.*)$/;

function indentOf(ws: string): number {
  let n = 0;
  for (const ch of ws) n += ch === "\t" ? 4 : 1;
  return n;
}

function isFenceClose(line: string, n: number): boolean {
  const m = /^ {0,3}(`{3,})\s*$/.exec(line);
  return m !== null && (m[1] ?? "").length >= n;
}

type CodeMatch = { block: Block; next: number };

/** A code block starting at line i, or null. */
function codeBlock(lines: string[], i: number): CodeMatch | null {
  const open = FENCE_OPEN.exec(lines[i] ?? "");
  if (!open) return null;
  const fence = open[1] ?? "";
  const rest = open[2] ?? "";
  const n = fence.length;
  const lang = LANG.exec(rest);
  if (lang) {
    // ```lang on its own line: the block runs to the closing fence, or to the end of the message.
    const body: string[] = [];
    let j = i + 1;
    while (j < lines.length && !isFenceClose(lines[j] ?? "", n)) body.push(lines[j++] ?? "");
    return {
      block: { type: "code_block", lang: lang[1] ? lang[1].toLowerCase() : null, text: body.join("\n") },
      next: Math.min(j + 1, lines.length),
    };
  }
  // ```text on the opening line, as Slack allows: the block closes where a line ends with the fence.
  const closing = new RegExp(`^(.*?)\`{${n},}\\s*$`);
  const same = closing.exec(rest);
  if (same && (same[1] ?? "").length) {
    return { block: { type: "code_block", lang: null, text: same[1] ?? "" }, next: i + 1 };
  }
  const body = [rest];
  for (let j = i + 1; j < lines.length; j++) {
    const end = closing.exec(lines[j] ?? "");
    if (end) {
      if ((end[1] ?? "").length) body.push(end[1] ?? "");
      return { block: { type: "code_block", lang: null, text: body.join("\n") }, next: j + 1 };
    }
    body.push(lines[j] ?? "");
  }
  return null;
}

function trimBlankEdges(lines: string[]): string[] {
  let a = 0;
  let b = lines.length;
  while (a < b && !(lines[a] ?? "").trim()) a++;
  while (b > a && !(lines[b - 1] ?? "").trim()) b--;
  return lines.slice(a, b);
}

function blocksOf(body: string, mentions: readonly MentionName[]): Block[] {
  const lines = body.split("\n");
  const blocks: Block[] = [];
  let para: string[] = [];
  const flushPara = () => {
    const kept = trimBlankEdges(para);
    para = [];
    if (kept.length) blocks.push({ type: "paragraph", children: inline(kept.join("\n"), true, mentions) });
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";

    const code = codeBlock(lines, i);
    if (code) {
      flushPara();
      blocks.push(code.block);
      i = code.next;
      continue;
    }

    if (QUOTE.test(line)) {
      flushPara();
      const quoted: string[] = [];
      let m = QUOTE.exec(lines[i] ?? "");
      while (i < lines.length && m) {
        quoted.push(m[1] ?? "");
        i++;
        m = i < lines.length ? QUOTE.exec(lines[i] ?? "") : null;
      }
      blocks.push({ type: "quote", children: inline(quoted.join("\n"), true, mentions) });
      continue;
    }

    const item = ITEM.exec(line);
    if (item) {
      flushPara();
      const { list, next } = listAt(lines, i, mentions);
      blocks.push(list);
      i = next;
      continue;
    }

    para.push(line);
    i++;
  }
  flushPara();
  return blocks;
}

type Draft = { lines: string[]; lists: List[]; subs: { draft: Draft; list: List }[] };

const isOrderedMarker = (marker: string) => marker !== "-" && marker !== "*";

/** A list starting at line i: a top level and at most one nested level. */
function listAt(
  lines: string[],
  start: number,
  mentions: readonly MentionName[],
): { list: List; next: number } {
  const first = ITEM.exec(lines[start] ?? "");
  if (!first) return { list: { type: "list", ordered: false, start: 1, items: [] }, next: start + 1 };
  const ordered = isOrderedMarker(first[2] ?? "");
  const top: Draft[] = [];
  let i = start;
  // The item the next continuation line belongs to.
  let current: Draft | null = null;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const m = ITEM.exec(line);
    if (m) {
      const marker = m[2] ?? "";
      const itemOrdered = isOrderedMarker(marker);
      const draft: Draft = { lines: [m[3] ?? ""], lists: [], subs: [] };
      const parent = top[top.length - 1];
      if (indentOf(m[1] ?? "") >= 2 && parent) {
        // Deeper items join the nested level. A nested item of the other kind starts a new nested
        // list under the same item.
        let list = parent.subs[parent.subs.length - 1]?.list;
        if (!list || list.ordered !== itemOrdered) {
          list = {
            type: "list",
            ordered: itemOrdered,
            start: itemOrdered ? Number.parseInt(marker, 10) : 1,
            items: [],
          };
          parent.lists.push(list);
        }
        parent.subs.push({ draft, list });
      } else {
        if (itemOrdered !== ordered) break;
        top.push(draft);
      }
      current = draft;
      i++;
      continue;
    }
    // An indented line that is not an item carries on the item before it.
    if (current && /^[ \t]{2,}\S/.test(line) && !FENCE_OPEN.test(line) && !QUOTE.test(line)) {
      current.lines.push(line.replace(/^[ \t]+/, ""));
      i++;
      continue;
    }
    break;
  }

  const toItem = (d: Draft): ListItem => ({
    children: inline(d.lines.join("\n"), true, mentions),
    lists: d.lists,
  });
  for (const d of top) for (const sub of d.subs) sub.list.items.push(toItem(sub.draft));
  return {
    list: {
      type: "list",
      ordered,
      start: ordered ? Number.parseInt(first[2] ?? "", 10) : 1,
      items: top.map(toItem),
    },
    next: i,
  };
}

export function parseUncached(body: string, opts: ParseOptions = {}): Doc {
  const text = body.includes("\r") ? body.replace(/\r\n?/g, "\n") : body;
  const mentions = opts.mentions ?? [];
  if (opts.format !== "md") {
    const children = inline(text, false, mentions);
    return { blocks: children.length ? [{ type: "paragraph", children }] : [] };
  }
  return { blocks: blocksOf(text, mentions) };
}

const CACHE_SIZE = 2000;
const cache = new Map<string, Doc>();

/**
 * Parses a message body. Results are kept for the most recent bodies, so a list that re-renders
 * does not parse the same message twice. Callers must not change the returned tree.
 */
export function parse(body: string, opts: ParseOptions = {}): Doc {
  const names = opts.mentions?.length
    ? opts.mentions.map((m) => `${m.id}\u0002${m.name}`).join("\u0003")
    : "";
  const key = `${opts.format ?? ""}\u0001${names}\u0001${body}`;
  const hit = cache.get(key);
  if (hit) {
    // Move it to the back so the oldest entry is the one dropped.
    cache.delete(key);
    cache.set(key, hit);
    return hit;
  }
  let doc: Doc;
  try {
    doc = parseUncached(body, opts);
  } catch {
    // The parser is written not to throw. Should it ever, the message still shows as plain text.
    doc = { blocks: body ? [{ type: "paragraph", children: [{ type: "text", text: body }] }] : [] };
  }
  cache.set(key, doc);
  if (cache.size > CACHE_SIZE) {
    const oldest = cache.keys().next();
    if (!oldest.done) cache.delete(oldest.value);
  }
  return doc;
}
